import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LocalStorageItem } from '@core/enums/local-storage-item';
import { BackendAPI } from '@core/models/backend-api';
import { LoginResponse } from '@core/models/login-response.model';
import {BehaviorSubject, map, Observable, tap} from 'rxjs';
import { Logger } from './logger.service';
import {ExternalPlannerLoginResponse} from '@core/models/external-planner-login-response.model';

class UserOptions {
	readonly isAgency: boolean = false;
}

export class AuthStatus {
	readonly isAuthenticated: boolean;
	readonly displayName: string;
	readonly lastLogin: string | undefined;
	readonly userOptions: UserOptions;

	constructor (isAuthenticated: boolean, displayName: string, lastLogin: string | undefined, userOptions: UserOptions) {
		this.isAuthenticated = isAuthenticated;
		this.displayName = displayName;
		this.lastLogin = lastLogin;
		this.userOptions = userOptions
	}
}

@Injectable({
	providedIn: 'root'
})
export class AuthService {

	private readonly COMPONENT_NAME = "AuthService";

	private readonly defaultAuthStatus: AuthStatus = new AuthStatus(false, "Not logged in", "", new UserOptions());
	readonly authStatus$: BehaviorSubject<AuthStatus> = new BehaviorSubject<AuthStatus>(this.defaultAuthStatus);

	constructor (private _logger: Logger, private _http: HttpClient) {

		let authStatus: AuthStatus | undefined = undefined;

		const jwt = sessionStorage.getItem(LocalStorageItem.JWT);
		const authState = sessionStorage.getItem(LocalStorageItem.AUTH_STATE);

		//TODO: add timestamp checkup to handle expired JWT situation before bothering the BE
		if (jwt && authState) {
			try {
				const authStateObject = JSON.parse(authState);
				if (authStateObject) {
					authStatus = authStateObject;
				}
			}
			catch (error) {
				this._logger.error(this.COMPONENT_NAME, `Cannot parse the authState string saved in the session storage: "${authState}"`);
			}
		}

		if (authStatus) {
			this.authStatus$.next(authStatus);
		}
		else {
			this.removeAuthData();
			this.authStatus$.next(this.defaultAuthStatus);
		}
	}

	login(email: string, password: string): Observable<void> {
		const url = BackendAPI.userLogin;
		const loginRequest = { email: email, password: password };

		return this._http.post<LoginResponse>(url, loginRequest).pipe(
			map((response) => {
				if (response && response.jwt) {
					const authStatus = new AuthStatus(true, response.firstName || "", response.lastLogin, new UserOptions());

					// store the data first and only then announce auth status change, to let dependent components ability to read the status
					this.storeAuthData(authStatus, response.jwt);
					this.authStatus$.next(authStatus);
					if (response.lastLogin) {
						this.setLastLogin(response.lastLogin)
					}
					this._logger.info(this.COMPONENT_NAME, `Login succeeded for email="${email}"`);
				}
			})
		);
	}

	authenticateWithToken(token: string): Observable<ExternalPlannerLoginResponse> {
		const url = BackendAPI.authenticateWithToken + token
		return this._http.post<ExternalPlannerLoginResponse>(url, null)
			.pipe(
				tap((response) => {
					if (response && response.jwt) {
						const authStatus = new AuthStatus(true, response.firstName || "", response.lastLogin, new UserOptions());

						// Store External Planner info into session storage
						this.setExternalPlannerInfo(response);
						// store the data first and only then announce auth status change, to let dependent components ability to read the status
						this.storeAuthData(authStatus, response.jwt);
						this.authStatus$.next(authStatus);
						if (response.lastLogin) {
							this.setLastLogin(response.lastLogin)
						}
						this._logger.info(this.COMPONENT_NAME, `Login succeeded for user="${response.firstName}"`);
					}
				})
			);
	}

	logout() {
		this.removeAuthData();
		this.removeUnnecessaryInfoFromSession()
		this.authStatus$.next(this.defaultAuthStatus);
	}

	get isAuthenticated(): boolean {
		return this.authStatus$.value.isAuthenticated;
	}

	private storeAuthData(authStatus: AuthStatus, jwt: string) {
		// JWT is used by CassHttpInterceptorService, so store it separately to make its reading simple
		sessionStorage.setItem(LocalStorageItem.AUTH_STATE, JSON.stringify(authStatus));
		sessionStorage.setItem(LocalStorageItem.JWT, jwt);
	}

	private removeAuthData() {
		sessionStorage.removeItem(LocalStorageItem.AUTH_STATE);
		sessionStorage.removeItem(LocalStorageItem.JWT);
	}

	private setLastLogin(lastLoginTime: string) {
		sessionStorage.setItem(LocalStorageItem.LAST_LOGIN, lastLoginTime);
	}

	private removeUnnecessaryInfoFromSession() {
		sessionStorage.removeItem(LocalStorageItem.LAST_LOGIN);
	}

	private setExternalPlannerInfo(externalPlanInfo: ExternalPlannerLoginResponse) {
		sessionStorage.setItem(LocalStorageItem.EXTERNAL_PLAN_INFO, JSON.stringify(externalPlanInfo))
	}

	get getExternalPlannerInfo(): ExternalPlannerLoginResponse | undefined {
		let externalPlanInfo = undefined
		externalPlanInfo = sessionStorage.getItem(LocalStorageItem.EXTERNAL_PLAN_INFO);
		return externalPlanInfo && JSON.parse(externalPlanInfo)
	}

	clearExternalPlannerInfo() {
		sessionStorage.removeItem(LocalStorageItem.EXTERNAL_PLAN_INFO);
	}

}
